import React from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';

export type AudioFeatureMenuItemKey = 'customize' | 'manageSources';

export type AudioFeatureMenuItem = {
	id: AudioFeatureMenuItemKey;
	onClick: () => void;
	icon?: React.ReactNode;
	isDisabled?: boolean;
	testId?: string;
	title: MessageDescriptor;
	description?: string;
	renderPanel?: {
		header: MessageDescriptor;
		body: () => React.ReactNode;
		onBackClick?: () => void;
	};
};

type AudioFeatureMenuGroupProps = {
	items: AudioFeatureMenuItem[];
};

export const AudioFeatureMenuGroup = ({ items }: AudioFeatureMenuGroupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { setActiveMenu } = useAudioPlayerStore();
	const { sendUIEvent } = usePlatformInstrumentation();

	const handleClick = (item: AudioFeatureMenuItem) => {
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'audioMenu',
			source: 'audioPlayer',
			attributes: {
				version: 'v3',
				menuItem: item.id,
			},
		});

		item.onClick();

		if (item.renderPanel) {
			setActiveMenu(item);
		}
	};

	return (
		<MenuGroup testId="audio-object-menu-group">
			<Section>
				{items.map((item) => (
					<ButtonItem
						iconBefore={item.icon}
						key={item.id}
						onClick={() => handleClick(item)}
						isDisabled={item.isDisabled}
						testId={item.testId}
						description={item.description}
					>
						<div>{formatMessage(item.title)}</div>
					</ButtonItem>
				))}
			</Section>
		</MenuGroup>
	);
};
